import { useEffect, useState, useRef } from 'react';

export default function SocialProof() {
  const [isVisible, setIsVisible] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const testimonials = [
    {
      quote: "FlexDown helped us manage over 20 properties seamlessly. The transparency and ease of use are unmatched.",
      author: "Samuel K.",
      role: "Property Owner, Accra",
      image: "https://public.readdy.ai/ai/img_res/c51d4489aba31796cf8a57841faf3472.jpg"
    },
    {
      quote: "The valuation insights gave us the confidence to make informed investment decisions. A game-changer for real estate in Ghana.",
      author: "Abena M.",
      role: "Real Estate Investor",
      image: "https://readdy.ai/api/search-image?query=Professional%20African%20businesswoman%20in%20elegant%20office%20environment%20wearing%20sophisticated%20business%20attire%20with%20confident%20demeanor%20against%20clean%20modern%20background&width=400&height=400&seq=testimonial-2&orientation=squarish"
    },
    {
      quote: "Secure transactions and verified listings made our property purchase stress-free. FlexDown is the future of real estate.",
      author: "Kwame A.",
      role: "First-time Buyer, Kumasi",
      image: "https://readdy.ai/api/search-image?query=Young%20professional%20African%20man%20in%20contemporary%20workspace%20wearing%20smart%20casual%20clothing%20with%20friendly%20approachable%20expression%20against%20bright%20clean%20background&width=400&height=400&seq=testimonial-3&orientation=squarish"
    },
    {
      quote: "Managing tenants and maintenance through one platform has saved us countless hours. Highly recommend FlexDown.",
      author: "Esi T.",
      role: "Property Manager",
      image: "https://readdy.ai/api/search-image?query=Professional%20African%20woman%20in%20modern%20business%20setting%20wearing%20professional%20attire%20with%20warm%20welcoming%20smile%20against%20minimalist%20clean%20background&width=400&height=400&seq=testimonial-4&orientation=squarish"
    },
    {
      quote: "The market data and analytics helped us price our properties competitively. FlexDown understands the Ghanaian market.",
      author: "Kofi D.",
      role: "Developer, Tema",
      image: "https://readdy.ai/api/search-image?query=Experienced%20African%20businessman%20in%20upscale%20office%20environment%20wearing%20executive%20business%20attire%20with%20authoritative%20confident%20presence%20against%20sophisticated%20clean%20background&width=400&height=400&seq=testimonial-5&orientation=squarish"
    }
  ];

  return (
    <section ref={sectionRef} className="py-32 bg-white overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12 mb-16">
        <div className="text-center">
          <h2 className="text-5xl font-semibold mb-6" style={{ color: '#0B1C2D' }}>
            Trusted by Property Owners Across Ghana
          </h2>
          <p className="text-xl max-w-2xl mx-auto" style={{ color: '#6B7A99' }}>
            Join hundreds of satisfied clients who have transformed their real estate experience
          </p>
        </div>
      </div>

      <div 
        ref={scrollRef}
        className="flex gap-6 px-6 overflow-x-auto scrollbar-hide hover:pause-animation"
        style={{
          animation: 'scroll 40s linear infinite',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        {[...testimonials, ...testimonials].map((testimonial, index) => (
          <div
            key={index}
            className={`flex-shrink-0 w-96 p-8 rounded-2xl transition-all duration-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
            style={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(14px)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 4px 24px rgba(30, 79, 255, 0.06)',
              transitionDelay: `${(index % testimonials.length) * 100}ms`
            }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                <img 
                  src={testimonial.image} 
                  alt={testimonial.author}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <h4 className="font-semibold text-lg" style={{ color: '#0B1C2D' }}>
                  {testimonial.author}
                </h4>
                <p className="text-sm" style={{ color: '#6B7A99' }}>
                  {testimonial.role}
                </p>
              </div>
            </div>
            
            <p className="text-base leading-relaxed" style={{ color: '#6B7A99' }}>
              "{testimonial.quote}"
            </p>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        .hover\\:pause-animation:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
}
